import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileText, Lightning, CurrencyDollar, Warning, ArrowRight, TrendUp, ArrowsClockwise } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";

const STATUS_COLORS = {
  Vigente: "bg-[#2A9D8F] text-white",
  Adjudicado: "bg-[#002FA7] text-white",
  Desierto: "bg-[#E63946] text-white",
};

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const navigate = useNavigate();

  const fetchStats = () => {
    api.get("/stats/dashboard")
      .then((res) => setStats(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchStats(); }, []);

  const handleSyncRecent = async () => {
    setSyncing(true);
    try {
      const res = await api.post("/scraper/sync-recent?days=1");
      const jobId = res.data.id;
      toast.success("Sincronización iniciada", {
        description: "Consultando últimas 24h en GuateCompras OCDS Live...",
      });
      // Poll job status every 2.5s up to 90s
      let elapsed = 0;
      const interval = setInterval(async () => {
        elapsed += 2.5;
        try {
          const j = await api.get(`/scraper/jobs/${jobId}`);
          if (j.data.status === "completed") {
            clearInterval(interval);
            setSyncing(false);
            toast.success(`Sincronización completada`, {
              description: `${j.data.tenders_found} licitaciones nuevas importadas`,
            });
            fetchStats();
          } else if (j.data.status === "failed") {
            clearInterval(interval);
            setSyncing(false);
            toast.error("Error en sincronización", {
              description: (j.data.errors || [])[0] || "Revisa el monitor de scrapers",
            });
          } else if (elapsed >= 90) {
            clearInterval(interval);
            setSyncing(false);
            toast.info("Sincronización en curso", {
              description: "Tarda más de lo esperado. Revisa el monitor para ver el progreso.",
            });
          }
        } catch (e) {
          clearInterval(interval);
          setSyncing(false);
        }
      }, 2500);
    } catch (e) {
      setSyncing(false);
      toast.error("Error al iniciar sincronización", {
        description: e.response?.data?.detail || "Intenta de nuevo",
      });
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <Skeleton className="h-10 w-48 rounded-none" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-1">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32 rounded-none" />)}
        </div>
        <Skeleton className="h-64 rounded-none" />
      </div>
    );
  }

  const kpis = [
    { label: "Licitaciones Hoy", value: stats?.tenders_today || 0, sub: `${stats?.total_tenders || 0} total`, icon: FileText, color: "#002FA7" },
    { label: "Scrapers Activos", value: stats?.active_scrapers || 0, sub: "Configuraciones", icon: Lightning, color: "#2A9D8F" },
    { label: "Monto Total GTQ", value: `Q${((stats?.total_amount_gtq || 0) / 1000000).toFixed(1)}M`, sub: "Acumulado", icon: CurrencyDollar, color: "#002FA7" },
    { label: "Alertas", value: stats?.alerts || 0, sub: "Jobs fallidos", icon: Warning, color: stats?.alerts > 0 ? "#E63946" : "#2A9D8F" },
  ];

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground text-sm mt-1">Resumen de licitaciones y actividad</p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="sync-recent-btn"
            variant="outline"
            disabled={syncing}
            className="rounded-none border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white"
            onClick={handleSyncRecent}
          >
            <ArrowsClockwise size={16} className={`mr-2 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Sincronizando..." : "Sincronizar 24h"}
          </Button>
          <Button
            data-testid="go-to-search"
            className="rounded-none bg-[#002FA7] hover:bg-[#001D66] text-white"
            onClick={() => navigate("/buscar")}
          >
            Buscar Licitaciones <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border">
        {kpis.map((kpi, i) => (
          <Card key={i} className="rounded-none shadow-none border-0 animate-fade-in-up" style={{ animationDelay: `${i * 80}ms` }}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{kpi.label}</p>
                  <p className="font-heading font-light text-4xl mt-2" style={{ color: kpi.color }} data-testid={`kpi-${i}`}>
                    {kpi.value}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1 font-mono">{kpi.sub}</p>
                </div>
                <kpi.icon size={24} weight="duotone" style={{ color: kpi.color }} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Chart + Vigentes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-border">
        <Card className="lg:col-span-2 rounded-none shadow-none border-0">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading font-bold text-sm tracking-tight flex items-center gap-2">
              <TrendUp size={18} weight="duotone" className="text-[#002FA7]" />
              Tendencia de Licitaciones (7 días)
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="h-56" data-testid="tender-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats?.tender_trends || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} stroke="#52525B" />
                  <YAxis tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} stroke="#52525B" />
                  <Tooltip
                    contentStyle={{ border: "1px solid #E4E4E7", borderRadius: 0, fontFamily: "IBM Plex Mono", fontSize: 12 }}
                  />
                  <Line type="monotone" dataKey="count" stroke="#002FA7" strokeWidth={2} dot={{ fill: "#002FA7", r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-none shadow-none border-0">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading font-bold text-sm tracking-tight">Resumen Rápido</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-[#002FA7]/5 border border-[#002FA7]/20">
              <p className="font-mono text-xs text-muted-foreground uppercase">Vigentes</p>
              <p className="font-heading font-light text-3xl text-[#002FA7]" data-testid="vigentes-count">{stats?.vigentes || 0}</p>
            </div>
            <div className="p-4 bg-[#2A9D8F]/5 border border-[#2A9D8F]/20">
              <p className="font-mono text-xs text-muted-foreground uppercase">Total Registradas</p>
              <p className="font-heading font-light text-3xl text-[#2A9D8F]">{stats?.total_tenders || 0}</p>
            </div>
            <div className="p-4 bg-secondary border border-border/40">
              <p className="font-mono text-xs text-muted-foreground uppercase">GuateCompras</p>
              <p className="text-sm font-medium mt-1">Fuente principal activa</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Tenders */}
      <Card className="rounded-none shadow-none border border-border/40" data-testid="recent-tenders-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading font-bold text-sm tracking-tight">Licitaciones Recientes</CardTitle>
            <Button variant="ghost" size="sm" className="rounded-none text-xs font-mono" onClick={() => navigate("/buscar")} data-testid="view-all-tenders">
              Ver todas <ArrowRight size={14} className="ml-1" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="font-mono text-xs uppercase tracking-wider">NOG</TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-wider">Título</TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-wider">Entidad</TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-wider text-right">Monto</TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-wider">Estado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(stats?.recent_tenders || []).slice(0, 8).map((t, i) => (
                <TableRow
                  key={t.id || i}
                  className="cursor-pointer hover:bg-secondary/50 transition-colors duration-150"
                  onClick={() => navigate(`/licitacion/${t.id}`)}
                  data-testid={`tender-row-${i}`}
                >
                  <TableCell className="font-mono text-xs text-muted-foreground">{t.nog}</TableCell>
                  <TableCell className="text-sm font-medium max-w-xs truncate">{t.title}</TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{t.entity_name}</TableCell>
                  <TableCell className="font-mono text-sm text-right">Q{(t.amount || 0).toLocaleString()}</TableCell>
                  <TableCell>
                    <Badge className={`rounded-none text-[10px] font-mono ${STATUS_COLORS[t.status] || "bg-secondary text-foreground"}`}>
                      {t.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
